import { Key, useMemo, useState } from 'react'
import { useCIMSModalValues } from 'components/CIMSModal/hooks/useCIMSModal'
import { useRouteMatches } from 'hooks/useRouteMatches'
import { useGetWorkflowPermissionsQuery } from 'schema'
import { createStrictContext } from 'utils/createContext'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { PermissionsContext } from './types'
import { usePermissionUsers } from './hooks/usePermissionUsers'
import { useSelectedPermissions } from './hooks/useSelectedPermissions'

const { Provider, useContextFn } =
  createStrictContext<PermissionsContext>('PermissionsContext')

export const usePermissionsContext = useContextFn

const WorkflowPermissionsProvider: React.FC<{ workflowId: string }> = ({
  children,
  workflowId,
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([])
  const addUsersModal = useCIMSModalValues()
  const {
    loading: usersLoading,
    userLabel,
    usersById,
    userTooltip,
  } = usePermissionUsers()
  const {
    data,
    loading: queryLoading,
    refetch,
  } = useGetWorkflowPermissionsQuery({
    ...DefaultQueryHookOptions,
    variables: { workflowId },
  })
  const records = useMemo(
    () => data?.listPermissions.records ?? [],
    [data?.listPermissions.records]
  )
  const fields = useMemo(
    () => data?.listFields.records ?? [],
    [data?.listFields.records]
  )
  const statuses = useMemo(
    () => data?.listStatuses.records ?? [],
    [data?.listStatuses.records]
  )
  const fieldMap = useMemo(
    () => new Map(fields.map((field) => [field.id, field])),
    [fields]
  )
  const statusMap = useMemo(
    () => new Map(statuses.map((status) => [status.id, status])),
    [statuses]
  )
  const selectedPermissions = useSelectedPermissions(records, selectedRowKeys)
  const fetching = queryLoading || usersLoading

  return (
    <Provider
      value={{
        fetching,
        metadata: {
          fieldMap,
          fields,
          statusMap,
          statuses,
        },
        modals: { addUsers: addUsersModal },
        records,
        refetch,
        selection: {
          records: selectedPermissions,
          rowKeys: selectedRowKeys,
          setRowKeys: setSelectedRowKeys,
        },
        users: {
          byId: usersById,
          label: userLabel,
          tooltip: userTooltip,
        },
        workflowId,
      }}
    >
      {children}
    </Provider>
  )
}

const PermissionsProvider: React.FC = ({ children }) => {
  const { param } = useRouteMatches()
  const workflowId = param('workflowId')!

  return (
    <WorkflowPermissionsProvider key={workflowId} workflowId={workflowId}>
      {children}
    </WorkflowPermissionsProvider>
  )
}

export default PermissionsProvider
